import React from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";

export interface LoginFormSystemValues {
  username: string;
  password: string;
}

interface LoginFormSystemProps {
  values: LoginFormSystemValues;
  onChange: (field: keyof LoginFormSystemValues, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSwitchToStandard: () => void;
  isLoading: boolean;
  error: string;
}

const LoginFormSystem: React.FC<LoginFormSystemProps> = ({
  values,
  onChange,
  onSubmit,
  onSwitchToStandard,
  isLoading,
}) => {
  const { username, password } = values;
  const disabled = isLoading || !username.trim() || !password.trim();

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="bg-error-50 border border-error-100 p-3.5 rounded-xl flex items-center gap-2.5 mb-2">
        <ShieldAlert className="w-4 h-4 text-error-500 flex-shrink-0" />
        <p className="text-sm text-error-700">System Administrator Access Only</p>
      </div>
      <div>
        <label htmlFor="sys-username" className="block text-sm font-medium text-gray-600 mb-1.5">
          Administrator Username
        </label>
        <input
          id="sys-username"
          type="text"
          value={username}
          onChange={(e) => onChange("username", e.target.value)}
          placeholder="Enter system username"
          className="input-field"
          disabled={isLoading}
          autoComplete="username"
        />
      </div>
      <div>
        <label htmlFor="sys-password" className="block text-sm font-medium text-gray-600 mb-1.5">
          Administrator Password
        </label>
        <input
          id="sys-password"
          type="password"
          value={password}
          onChange={(e) => onChange("password", e.target.value)}
          placeholder="Enter system password"
          className="input-field"
          disabled={isLoading}
          autoComplete="current-password"
        />
      </div>
      <p className="text-xs text-gray-500 text-center">
        By signing in, you agree to our{" "}
        <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-700 underline">
          Privacy Policy
        </Link>
      </p>
      <button
        type="submit"
        disabled={disabled}
        className="w-full btn-error mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? "Signing in..." : "Access System Administration"}
      </button>

      <div className="text-center pt-2">
        <button
          type="button"
          onClick={onSwitchToStandard}
          className="text-gray-400 hover:text-gray-600 text-sm transition-colors"
        >
          Back to employee/admin login
        </button>
      </div>
    </form>
  );
};

export default LoginFormSystem;
